import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { EnvironmentService } from '@core/services/environment.service';
import { fixAssetUrl } from '@features/contentful/helpers/assetHelpers';
import {
    CropFocus,
    ImageFit,
} from '@features/lazy-image/components/lazy-picture/lazy-picture.component';
import { LazysizesService } from '@features/lazy-image/services/lazysizes.service';
import { Asset } from '@generated/graphql';

export interface AssetImageConfiguration {
    image: Asset;
    width: number;
    width2x?: number;
    width3x?: number;
    quality?: number;
    ratioWidth?: number;
    ratioHeight?: number;
    fit?: ImageFit;
    focus?: CropFocus;
    format?: string;
    placeholderColor?: string;
}

interface Width {
    w: number;
    h?: number;
}

@Component({
    selector: 'app-lazy-asset-lazy',
    imports: [CommonModule],
    templateUrl: './lazy-asset-lazy.component.html',
    host: { ngSkipHydration: 'true' },
})
export class LazyAssetLazyComponent implements OnChanges {
    @Input() configuration: AssetImageConfiguration;
    @Input() additionalClasses: string[] | string = [];
    @Input() lazy: boolean = true;

    svgPlaceholder: SafeUrl;
    srcSet: string;
    defaultSrc: string;
    altText: string;

    constructor(
        private environmentService: EnvironmentService,
        private lazysizesService: LazysizesService,
        private sanitizer: DomSanitizer
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.configuration && this.configuration?.image) {
            this.svgPlaceholder = this.generateSvgPlaceholder();
            const [srcSet, defaultSrc] = this.generateSrcSet(
                this.configuration
            );
            this.srcSet = srcSet;
            this.defaultSrc = defaultSrc;
            this.altText =
                this.configuration?.image?.description ||
                this.configuration?.image?.title ||
                '';
        }
    }

    generateSrcSet(configuration: AssetImageConfiguration): [string, string] {
        let imageBaseUrl = fixAssetUrl(configuration.image?.url || '');
        if (
            imageBaseUrl.endsWith('.svg') ||
            configuration.image.sys.id === 'FAKE'
        ) {
            return [imageBaseUrl, imageBaseUrl];
        }
        const options = [];

        if (configuration.quality) {
            options.push(`q=${configuration.quality}`);
        } else {
            options.push(`q=80`);
        }
        if (configuration.fit) {
            options.push(`fit=${configuration.fit}`);
        } else {
            options.push('fit=fill');
        }
        if (configuration.focus) {
            options.push(`f=${configuration.focus}`);
        } else {
            options.push('f=center');
        }
        const urlWithOptions = `${imageBaseUrl}?${options.join('&')}`;
        const width1x = this.generateWidthQuery(configuration.width);
        const width2x = this.generateWidthQuery(
            configuration.width2x || configuration.width
        );
        const width3x = this.generateWidthQuery(
            configuration.width3x ||
                configuration.width2x ||
                configuration.width
        );
        let defaultSrc = `${urlWithOptions}&${width1x}`;
        return [
            `${urlWithOptions}&${width1x} 1x, ${urlWithOptions}&${width2x} 2x, ${urlWithOptions}&${width3x} 3x`,
            defaultSrc,
        ];
    }

    generateWidthQuery(width: number) {
        const computedWidth = this.generateWidth(width);
        return Object.entries(computedWidth)
            .map(items => items.join('='))
            .join('&');
    }

    generateWidth(width: number): Width {
        if (this.configuration.ratioHeight && this.configuration.ratioWidth) {
            const ratio =
                this.configuration.ratioHeight / this.configuration.ratioWidth;
            return { w: width, h: Math.round(width * ratio) };
        } else {
            const ratio =
                this.configuration.image.height /
                this.configuration.image.width;
            return { w: width, h: Math.round(width * ratio) };
        }
    }

    generateSvgPlaceholder() {
        const width = this.generateWidth(this.configuration.width);
        const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${
            width.w
        } ${width.h}'><rect height='100%' style='fill:${
            this.configuration.placeholderColor || '#e9e9e9'
        }' width='100%' x='0' y='0' /></svg>`;
        let encodedSvg = '';
        if (this.environmentService.isBrowser()) {
            encodedSvg = btoa(svg);
        } else {
            encodedSvg = Buffer.from(svg, 'binary').toString('base64');
        }
        return this.sanitizer.bypassSecurityTrustUrl(
            `data:image/svg+xml;base64,${encodedSvg}`
        );
    }
}
